<template>
  <div class="waajo-kanban-board-wrap" :class="{ 'is-dragging': isDragging }">
    <div v-if="moveError" class="waajo-kanban-board-error">
      <i class="fa fa-exclamation-triangle"></i>
      <span>{{ moveError }}</span>
      <button type="button" @click="moveError = ''">Tutup</button>
    </div>

    <div class="waajo-kanban-board">
      <ticket-kanban-column
        v-for="column in orderedColumns"
        :key="column.status"
        :column="column"
        :drag-disabled="dragLocked"
        :moving-id="movingId"
        :filter-active="filterActive"
        @load-more="loadMore"
        @retry="retryColumn"
        @open="$emit('open', $event)"
        @move="moveViaMenu"
        @drag-start="onDragStart"
        @drag-end="onDragEnd"
      />
    </div>
  </div>
</template>

<script>
import TicketKanbanColumn from './TicketKanbanColumn.vue'
import { extractApiErrorMessage } from '@/utils/ticket'
import {
  TICKET_KANBAN_LIMIT,
  appendTicketKanbanItems,
  buildTicketKanbanParams,
  ticketKanbanCursorFromItems,
  ticketKanbanHasActiveFilter,
  ticketKanbanMatchesFilters,
  ticketKanbanSameOrder
} from '@/utils/ticketKanban'

const KANBAN_COLUMNS = [
  {
    status: 'open',
    label: 'Open',
    color: 'open',
    icon: 'fa fa-envelope-open-o',
    description: 'Menunggu ditangani'
  },
  {
    status: 'in_progress',
    label: 'In Progress',
    color: 'progress',
    icon: 'fa fa-spinner',
    description: 'Sedang ditangani'
  },
  {
    status: 'resolved',
    label: 'Resolved',
    color: 'resolved',
    icon: 'fa fa-check-circle',
    description: 'Selesai ditangani'
  }
]

const STATUS_KEYS = KANBAN_COLUMNS.map(item => item.status)
const FILTER_DEBOUNCE = 350

function createColumn(config) {
  return {
    status: config.status,
    label: config.label,
    color: config.color,
    icon: config.icon,
    description: config.description,
    items: [],
    total: 0,
    hasMore: false,
    cursor: null,
    loading: false,
    loadingMore: false,
    refreshing: false,
    loaded: false,
    error: '',
    requestId: 0
  }
}

export default {
  name: 'TicketKanbanBoard',
  components: {
    TicketKanbanColumn
  },
  props: {
    filters: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    const columns = {}
    KANBAN_COLUMNS.forEach(config => {
      columns[config.status] = createColumn(config)
    })
    return {
      limit: TICKET_KANBAN_LIMIT,
      columns,
      filterTimer: null,
      dragLocked: false,
      isDragging: false,
      movingId: null,
      moveError: '',
      dragSnapshot: null
    }
  },
  computed: {
    orderedColumns() {
      return STATUS_KEYS.map(key => this.columns[key])
    },
    filterActive() {
      return ticketKanbanHasActiveFilter(this.filters)
    }
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        this.scheduleReload()
      }
    }
  },
  mounted() {
    this.reloadAll()
  },
  beforeDestroy() {
    if (this.filterTimer) clearTimeout(this.filterTimer)
    this.filterTimer = null
    this.isDragging = false
    // Naikkan request id agar response yang masih berjalan tidak menulis state.
    STATUS_KEYS.forEach(key => {
      this.columns[key].requestId += 1
    })
  },
  methods: {
    scheduleReload() {
      if (this.filterTimer) clearTimeout(this.filterTimer)
      this.filterTimer = setTimeout(() => {
        this.filterTimer = null
        this.reloadAll()
      }, FILTER_DEBOUNCE)
    },
    buildParams(status, cursor) {
      return buildTicketKanbanParams(this.filters, status, cursor, this.limit)
    },
    reloadAll() {
      STATUS_KEYS.forEach(status => this.fetchColumn(status, { reset: true }))
    },
    refreshStatus(status) {
      this.fetchColumn(status, { reset: true, silent: true })
    },
    retryColumn(status) {
      const column = this.columns[status]
      if (!column) return
      this.fetchColumn(status, { reset: true, silent: column.items.length > 0 })
    },
    refreshTicketColumns() {
      // Dipakai parent setelah create/edit: muat ulang seluruh kolom tanpa
      // mengosongkan tampilan yang sudah ada.
      STATUS_KEYS.forEach(status => this.fetchColumn(status, { reset: true, silent: true }))
    },
    async fetchColumn(status, options) {
      const settings = options || {}
      const column = this.columns[status]
      if (!column) return

      const requestId = column.requestId + 1
      column.requestId = requestId
      column.error = ''
      const previousCursor = column.cursor

      if (settings.reset) {
        column.cursor = null
        if (settings.silent) {
          column.refreshing = true
        } else {
          column.loading = true
          column.items = []
          column.total = 0
          column.hasMore = false
        }
      } else {
        column.loadingMore = true
      }

      const cursor = settings.reset ? '' : column.cursor

      try {
        const response = await this.$store.dispatch(
          'ticket/kanban',
          this.buildParams(status, cursor)
        )
        if (requestId !== column.requestId) return
        const payload = response && response.data ? response.data : {}
        const items = Array.isArray(payload.items) ? payload.items : []

        if (settings.reset) {
          column.items = items
        } else {
          column.items = appendTicketKanbanItems(column.items, items)
        }
        column.total = Number(payload.total) || 0
        column.hasMore = !!payload.has_more
        column.cursor = payload.next_cursor || null
        column.loaded = true
      } catch (error) {
        if (requestId !== column.requestId) return
        column.error = extractApiErrorMessage(
          error,
          'Gagal memuat tiket ' + column.label + '.'
        )
        if (settings.reset && !settings.silent) {
          column.items = []
          column.total = 0
          column.hasMore = false
        } else if (settings.reset && settings.silent) {
          column.cursor = previousCursor
        }
      } finally {
        if (requestId === column.requestId) {
          column.loading = false
          column.loadingMore = false
          column.refreshing = false
        }
      }
    },
    loadMore(status) {
      const column = this.columns[status]
      if (!column) return
      if (column.loading || column.loadingMore || !column.hasMore || !column.cursor) return
      this.fetchColumn(status, { reset: false })
    },
    ticketIdFromEvent(event) {
      if (!event || !event.item) return 0
      const value = event.item.getAttribute('data-ticket-id')
      return value ? Number(value) : 0
    },
    targetStatusFromEvent(event, fallback) {
      if (!event || !event.to || typeof event.to.getAttribute !== 'function') return fallback
      return event.to.getAttribute('data-status') || fallback
    },
    takeSnapshot() {
      const snapshot = {}
      STATUS_KEYS.forEach(status => {
        const column = this.columns[status]
        snapshot[status] = {
          items: column.items.slice(),
          total: column.total,
          hasMore: column.hasMore,
          cursor: column.cursor
        }
      })
      return snapshot
    },
    restoreSnapshot(snapshot) {
      if (!snapshot) return
      STATUS_KEYS.forEach(status => {
        const saved = snapshot[status]
        if (!saved) return
        const column = this.columns[status]
        column.items = saved.items.slice()
        column.total = saved.total
        column.hasMore = saved.hasMore
        column.cursor = saved.cursor
      })
    },
    onDragStart(status, event) {
      if (this.dragLocked) return
      this.moveError = ''
      this.isDragging = true
      this.dragSnapshot = this.takeSnapshot()
    },
    onDragEnd(fromStatus, event) {
      this.isDragging = false
      const snapshot = this.dragSnapshot
      this.dragSnapshot = null
      if (this.dragLocked) return
      if (!event) return

      const ticketId = this.ticketIdFromEvent(event)
      if (!ticketId) {
        this.restoreSnapshot(snapshot)
        this.movingId = null
        return
      }
      const toStatus = this.targetStatusFromEvent(event, fromStatus)
      const targetColumn = this.columns[toStatus]
      if (!targetColumn) {
        this.restoreSnapshot(snapshot)
        this.movingId = null
        return
      }

      const newIndex = targetColumn.items.findIndex(
        ticket => String(ticket.id) === String(ticketId)
      )
      if (newIndex === -1) {
        this.restoreSnapshot(snapshot)
        this.movingId = null
        return
      }

      if (
        fromStatus === toStatus &&
        snapshot &&
        ticketKanbanSameOrder(snapshot[fromStatus].items, targetColumn.items)
      ) {
        this.movingId = null
        return
      }

      const previous = newIndex > 0 ? targetColumn.items[newIndex - 1] : null
      const next = newIndex < targetColumn.items.length - 1
        ? targetColumn.items[newIndex + 1]
        : null

      this.submitMove({
        ticketId,
        fromStatus,
        toStatus,
        previousId: previous ? previous.id : 0,
        nextId: next ? next.id : 0,
        snapshot,
        mode: 'drag'
      })
    },
    moveViaMenu(payload) {
      if (this.dragLocked) return
      const ticket = payload && payload.ticket ? payload.ticket : null
      const toStatus = payload ? payload.status : ''
      if (!ticket || !ticket.id || !this.columns[toStatus]) return
      if (ticket.status === toStatus) return

      const fromStatus = this.statusOfTicket(ticket.id) || ticket.status
      if (!this.columns[fromStatus] || fromStatus === toStatus) return

      const snapshot = this.takeSnapshot()
      const source = this.columns[fromStatus]
      const target = this.columns[toStatus]
      const index = source.items.findIndex(item => String(item.id) === String(ticket.id))
      if (index !== -1) source.items.splice(index, 1)
      // Total count diperbarui setelah backend sukses (applyMoveResult) agar
      // tidak terjadi pengurangan ganda.
      target.items.push(Object.assign({}, ticket, { status: toStatus }))

      this.submitMove({
        ticketId: ticket.id,
        fromStatus,
        toStatus,
        previousId: 0,
        nextId: 0,
        snapshot,
        mode: 'menu'
      })
    },
    statusOfTicket(ticketId) {
      for (let index = 0; index < STATUS_KEYS.length; index += 1) {
        const status = STATUS_KEYS[index]
        const found = this.columns[status].items.some(
          ticket => String(ticket.id) === String(ticketId)
        )
        if (found) return status
      }
      return ''
    },
    async submitMove(options) {
      if (this.dragLocked) return
      this.dragLocked = true
      this.movingId = options.ticketId
      this.moveError = ''

      try {
        const response = await this.$store.dispatch('ticket/kanbanReorder', {
          ticket_id: options.ticketId,
          target_status: options.toStatus,
          previous_ticket_id: options.previousId || 0,
          next_ticket_id: options.nextId || 0
        })
        const payload = response && response.data ? response.data : {}
        this.applyMoveResult(options, payload)
        this.$emit('changed', {
          type: options.fromStatus === options.toStatus ? 'reorder' : 'move',
          ticketId: options.ticketId,
          fromStatus: options.fromStatus,
          toStatus: options.toStatus
        })
      } catch (error) {
        this.restoreSnapshot(options.snapshot)
        this.moveError = extractApiErrorMessage(
          error,
          'Gagal menyimpan urutan tiket. Urutan dikembalikan ke posisi sebelumnya.'
        )
        this.$fire({
          title: 'Urutan Gagal Disimpan',
          text: this.moveError,
          type: 'error',
          timer: 4000
        })
      } finally {
        this.dragLocked = false
        this.movingId = null
      }
    },
    applyMoveResult(options, payload) {
      const fromColumn = this.columns[options.fromStatus]
      const targetColumn = this.columns[options.toStatus]
      const moved = targetColumn.items.find(
        ticket => String(ticket.id) === String(options.ticketId)
      )

      if (moved) {
        moved.status = payload.status || options.toStatus
        moved.kanban_position = Number(payload.kanban_position) || moved.kanban_position
        if (payload.resolved_datetime !== undefined) {
          moved.resolved_datetime = Number(payload.resolved_datetime) || 0
        }
        if (payload.update_datetime) {
          moved.update_datetime = Number(payload.update_datetime)
        }
      }

      if (options.fromStatus !== options.toStatus) {
        fromColumn.total = Math.max(0, fromColumn.total - 1)
        targetColumn.total += 1
        fromColumn.cursor = this.cursorFromLast(fromColumn)
        fromColumn.hasMore = fromColumn.items.length < fromColumn.total
      }

      // Reindex server mengubah posisi seluruh kolom; muat ulang agar cursor
      // pagination dan urutan tetap akurat.
      if (payload.reindexed) {
        this.fetchColumn(options.toStatus, { reset: true, silent: true })
        return
      }

      if (options.mode === 'menu') {
        this.fetchColumn(options.toStatus, { reset: true, silent: true })
        return
      }

      if (targetColumn.items.length > this.limit) {
        this.fetchColumn(options.toStatus, { reset: true, silent: true })
        return
      }

      targetColumn.cursor = this.cursorFromLast(targetColumn)
      targetColumn.hasMore = targetColumn.items.length < targetColumn.total
    },
    cursorFromLast(column) {
      return ticketKanbanCursorFromItems(column.items)
    },
    matchesFilters(ticket) {
      return ticketKanbanMatchesFilters(ticket, this.filters)
    },
    // Dipakai parent setelah detail/edit tiket berubah.
    applyTicketUpdate(ticket) {
      if (!ticket || !ticket.id) return
      const status = this.statusOfTicket(ticket.id)
      if (!status) return

      const column = this.columns[status]
      const index = column.items.findIndex(
        item => String(item.id) === String(ticket.id)
      )
      if (index === -1) return

      if (ticket.status && ticket.status !== status && this.columns[ticket.status]) {
        column.items.splice(index, 1)
        column.total = Math.max(0, column.total - 1)
        this.fetchColumn(ticket.status, { reset: true, silent: true })
        return
      }

      Object.assign(column.items[index], ticket)
      if (!this.matchesFilters(ticket)) {
        column.items.splice(index, 1)
        column.total = Math.max(0, column.total - 1)
      }
    }
  }
}
</script>

<style scoped>
.waajo-kanban-board-wrap {
  width: 100%;
}
.waajo-kanban-board-wrap.is-dragging,
.waajo-kanban-board-wrap.is-dragging >>> * {
  cursor: grabbing !important;
  user-select: none;
  -webkit-user-select: none;
}
.waajo-kanban-board-error {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 13px;
  margin-bottom: 12px;
  border: 1px solid #fecaca;
  border-radius: 9px;
  background: #fef2f2;
  color: #b91c1c;
  font-size: 12px;
  line-height: 1.5;
}
.waajo-kanban-board-error button {
  margin-left: auto;
  border: 0;
  background: transparent;
  color: #b91c1c;
  font-size: 11px;
  font-weight: 700;
  text-decoration: underline;
}
.waajo-kanban-board {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  align-items: start;
}
@media (max-width: 1100px) {
  .waajo-kanban-board {
    grid-auto-flow: column;
    grid-auto-columns: minmax(290px, 1fr);
    grid-template-columns: none;
    overflow-x: auto;
    padding-bottom: 6px;
    -webkit-overflow-scrolling: touch;
  }
}
@media (max-width: 640px) {
  .waajo-kanban-board {
    grid-auto-columns: minmax(265px, 1fr);
    gap: 10px;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/ticket/components/TicketKanbanBoard.vue